import { envString, envUrl } from "./portfolioEnv";

// Portfolio data — set .env (see .env.example) for contact, demos, repos, and education overrides.

const fallbackGithubProfile = "https://github.com/arifwahyudi";
const githubProfile = envString("REACT_APP_GITHUB_URL", fallbackGithubProfile);

export const portfolioData = {
  // Personal Info
  name: envString("REACT_APP_DISPLAY_NAME", "Arif Wahyudi"),
  title: envString(
    "REACT_APP_JOB_TITLE",
    "IT Manager & Full Stack Developer"
  ),
  tagline: envString(
    "REACT_APP_TAGLINE",
    "I work where reliable operations meet thoughtful software—leading IT and infrastructure with the same rigor I bring to full-stack builds. The aim is simple: systems that stay calm under load, scale with the organization, and earn their place in the business."
  ),
  bio: envString(
    "REACT_APP_BIO",
    "I am a technology professional rooted in IT operations, infrastructure, and software development. Today I serve as an Information Technology Manager while continuing to ship digital solutions as a Full Stack Developer.\n\nMy path began in technical support and system administration—grounding in troubleshooting, infrastructure, and execution you can trust. That foundation widened into IT management, monitoring, and steady process improvement.\n\nWhat sets my profile apart is the pairing of operational leadership with hands-on engineering. I do not only run systems—I design and build them. I gravitate toward pragmatic, scalable work that changes how teams operate day to day."
  ),
  email: envString("REACT_APP_CONTACT_EMAIL", "contact@ariftech.my.id"),
  phone: envString("REACT_APP_CONTACT_PHONE", "+62 812-1647-1828"),
  location: envString("REACT_APP_CONTACT_LOCATION", "Kota Malang, Indonesia"),
  whatsapp: envString("REACT_APP_WHATSAPP_NUMBER", "6281216471828"),
  availability: envString(
    "REACT_APP_AVAILABILITY",
    "Open to selective collaborations, technical conversations, and well-scoped engineering engagements."
  ),

  stats: [
    { label: "Years in technology", value: "10+" },
    { label: "Operating scope", value: "Ops · Infra" },
    { label: "Build practice", value: "Full stack" },
    { label: "Posture", value: "Hands-on" },
  ],

  social: {
    github: envUrl("REACT_APP_SOCIAL_GITHUB") || githubProfile,
    linkedin: envString(
      "REACT_APP_SOCIAL_LINKEDIN",
      "https://www.linkedin.com/in/arif-wahyudi-3a7829224"
    ),
    twitter: envString("REACT_APP_SOCIAL_TWITTER", "https://twitter.com"),
    dribbble: envString("REACT_APP_SOCIAL_DRIBBBLE", "https://dribbble.com"),
  },

  // All projects — categorized across portfolio & freelance work.
  projects: [
    // ── HRMS ──────────────────────────────────────────────
    {
      id: 1,
      title: "HRMS Dashboard (React Demo)",
      description:
        "Self-contained HRIS-style UI: teal sidebar, dashboard with inbox and attendance widgets, employee directory table, and placeholder views for payroll, recruitment, and settings. Mock data only, embedded directly in the portfolio.",
      technologies: ["React", "Tailwind CSS", "Radix UI"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: envUrl("REACT_APP_REPO_HRMS") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_HRMS") || "/demo/hrms",
      category: "HRMS",
      source: "demo",
      features: [
        "Dashboard with attendance & inbox widgets",
        "Clock-in / clock-out (local demo state)",
        "Employee directory table with filters",
        "Multi-screen navigation shell",
      ],
    },
    {
      id: 14,
      title: "HR Platform — Full Stack HCM",
      description:
        "Production-grade HR & workforce management monorepo. NestJS REST API + Next.js admin panel covering the full employee lifecycle: onboarding, attendance, leave, payroll, recruitment, performance appraisals, and multi-tenant analytics.",
      technologies: ["Next.js", "NestJS", "TypeScript", "MySQL", "Prisma", "Docker"],
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "HRMS",
      source: "portfolio",
      features: [
        "Employee lifecycle & onboarding",
        "Attendance, leave & payroll engine",
        "Recruitment pipeline & appraisals",
        "Multi-tenant with analytics dashboard",
      ],
    },

    // ── Booking ───────────────────────────────────────────
    {
      id: 2,
      title: "Co-working Space Booking",
      description:
        "Interactive booking prototype for meeting rooms and desks: weekday strip, room cards with capacity, hourly pricing, amenity tags, and available vs. fully-booked states—focused on scheduling UX.",
      technologies: ["React", "Tailwind CSS"],
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop",
      previewLayout: "booking",
      repoUrl: envUrl("REACT_APP_REPO_BOOKING") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_BOOKING") || "/demo/booking",
      category: "Booking",
      source: "demo",
      features: [
        "Day-based slot picker",
        "Room grid with capacity & pricing",
        "Availability & disabled states",
        "Amenity chips on cards",
      ],
    },

    // ── IT Operations ─────────────────────────────────────
    {
      id: 3,
      title: "WorkHub — IT Operations Console",
      description:
        "Deep-navigation mock inspired by ITSM tools: expandable sidebar groups, service and asset-style menus, notification chrome, and a dark console layout—implemented as a self-contained React demo.",
      technologies: ["React", "Tailwind CSS", "Radix UI"],
      image: "https://images.unsplash.com/photo-1629904853893-c2c8981a1dc5?w=800&h=600&fit=crop",
      previewLayout: "workhub",
      repoUrl: envUrl("REACT_APP_REPO_WORKHUB") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_WORKHUB") || "/demo/workhub",
      category: "IT Operations",
      source: "demo",
      features: [
        "Collapsible multi-level sidebar",
        "Service & asset-style information architecture",
        "Search & alerts top-bar pattern",
        "Responsive shell & content panels",
      ],
    },
    {
      id: 21,
      title: "Pentest — Web Security Assessment Tool",
      description:
        "Professional full-stack security testing platform for authorized penetration testing. Covers OWASP Top 10 checks, TLS/SSL analysis, cookie audit, SSRF protection, domain verification, and generates PDF assessment reports.",
      technologies: ["Next.js", "TypeScript", "Prisma", "PostgreSQL", "NextAuth.js"],
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&h=600&fit=crop",
      previewLayout: "workhub",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "IT Operations",
      source: "portfolio",
      features: [
        "OWASP Top 10 automated checks",
        "TLS/SSL & cookie security audit",
        "Multi-tenant with 5 role levels",
        "PDF report generation",
      ],
    },
    {
      id: 24,
      title: "Grafana + Prometheus Monitoring Stack",
      description:
        "Infrastructure monitoring stack deployed via Docker Compose: Prometheus metrics collection, Grafana dashboards, Node Exporter for host metrics, AlertManager with Telegram notifications, and Uptime Kuma for endpoint availability.",
      technologies: ["Prometheus", "Grafana", "Docker", "AlertManager", "Uptime Kuma"],
      image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&h=600&fit=crop",
      previewLayout: "workhub",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "DevOps",
      source: "portfolio",
      features: [
        "Prometheus + Node Exporter metrics",
        "Grafana pre-built dashboards",
        "Telegram alerting via AlertManager",
        "Uptime Kuma endpoint monitoring",
      ],
    },

    // ── Travel ────────────────────────────────────────────
    {
      id: 4,
      title: "Asteria Voyage — Travel & Tour",
      description:
        "Luxury travel concierge website shipped as a static bundle: hero slider, destination cards, signature tour packages, white-glove service overview, testimonials, budget estimator, and WhatsApp lead generation. Embedded in the portfolio via iframe.",
      technologies: ["HTML", "CSS", "JavaScript"],
      image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&h=600&fit=crop",
      previewLayout: "travel",
      repoUrl: envUrl("REACT_APP_REPO_TRAVEL") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_TRAVEL") || "/demo/travel",
      category: "Travel",
      source: "demo",
      features: [
        "Hero slider with destination highlights",
        "Signature tour packages & pricing",
        "Budget estimator & FAQ",
        "WhatsApp lead generation integration",
      ],
    },

    // ── Finance ───────────────────────────────────────────
    {
      id: 5,
      title: "Personal Finance Planner",
      description:
        "Multi-section finance workspace: Recharts cashflow dashboard, transaction ledger, accounts & categories, budgets, allocation calculator, savings goals, recurring bills, assets, and CSV-style reporting—all client-side mock data.",
      technologies: ["React", "Recharts", "Tailwind CSS"],
      image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&h=600&fit=crop",
      previewLayout: "finance",
      repoUrl: envUrl("REACT_APP_REPO_FINANCE") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_FINANCE") || "/demo/finance",
      category: "Finance",
      source: "demo",
      features: [
        "Cashflow dashboard & charts",
        "Transaction ledger with categories",
        "Budget, goals & recurring expenses",
        "Reports & spreadsheet import UI",
      ],
    },
    {
      id: 12,
      title: "MT5 Trading Engine (MQL5)",
      description:
        "Modular automated trading engine for MetaTrader 5 implementing Smart Money Concept (SMC) analysis across three scaling strategies: Macro, Micro, and Nano timeframes. Includes break validation, configurable buffers, and Python export for backtesting.",
      technologies: ["MQL5", "MetaTrader 5", "Python"],
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&h=600&fit=crop",
      previewLayout: "finance",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Finance",
      source: "freelance",
      features: [
        "Smart Money Concept (SMC) analysis",
        "Macro / Micro / Nano strategy modules",
        "Break validator & configurable buffers",
        "Python export for backtesting",
      ],
    },

    // ── E-Commerce ────────────────────────────────────────
    {
      id: 6,
      title: "LittleNest Baby Shop",
      description:
        "Full-stack e-commerce for baby essentials: Next.js storefront with Prisma + PostgreSQL, product catalog, cart, checkout with Midtrans payment, vouchers, order tracking, content banners, and a full admin back office.",
      technologies: ["Next.js", "TypeScript", "Prisma", "PostgreSQL", "Midtrans", "Zustand"],
      image: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=800&h=600&fit=crop",
      previewLayout: "shop",
      repoUrl: envUrl("REACT_APP_REPO_BABYSHOP") || githubProfile,
      liveUrl: envUrl("REACT_APP_LIVE_BABYSHOP") || "/demo/shop",
      category: "E-Commerce",
      source: "portfolio",
      features: [
        "Product catalog with filters & search",
        "Cart, checkout & Midtrans payment",
        "Vouchers, promos & content banners",
        "Admin back office for products & orders",
      ],
    },
    {
      id: 17,
      title: "Son Ga Korin — Korean Food Market",
      description:
        "Full-stack Korean food market: Next.js 15 storefront with mega menu, product filtering and sorting, cart, multi-payment checkout (Midtrans, QRIS, manual transfer, Xendit), and a Prisma + PostgreSQL backend.",
      technologies: ["Next.js", "TypeScript", "Prisma", "PostgreSQL", "Midtrans", "Xendit"],
      image: "https://images.unsplash.com/photo-1590437084089-9f5ae1500176?w=800&h=600&fit=crop",
      previewLayout: "shop",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "E-Commerce",
      source: "portfolio",
      features: [
        "Mega menu & advanced product filters",
        "Multi-payment gateway (Midtrans, Xendit, QRIS)",
        "Cart & checkout flow",
        "Prisma ORM + PostgreSQL backend",
      ],
    },
    {
      id: 18,
      title: "BeliGo — Laravel E-Commerce Platform",
      description:
        "Production-grade monolithic e-commerce built on Laravel 12 and MySQL with Elasticsearch for product search. Supports multi-vendor product management, inventory, shipping, payment (Stripe, PayPal, Razorpay), RMA, and a full admin back office.",
      technologies: ["Laravel 12", "PHP 8.3", "MySQL", "Elasticsearch", "Stripe"],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      previewLayout: "shop",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "E-Commerce",
      source: "portfolio",
      features: [
        "Elasticsearch-powered product search",
        "Multi-vendor product & inventory management",
        "Stripe, PayPal & Razorpay payments",
        "RMA & full admin back office",
      ],
    },
    {
      id: 19,
      title: "Bagisto — Flutter Mobile E-Commerce",
      description:
        "Cross-platform mobile storefront (iOS/Android/Web) built with Flutter 3, consuming a GraphQL API. Features Firebase push notifications, ML Kit image recognition for product discovery, camera integration, and in-app update support.",
      technologies: ["Flutter", "GraphQL", "Firebase", "ML Kit"],
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop",
      previewLayout: "shop",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Mobile App",
      source: "portfolio",
      features: [
        "iOS, Android & Web from one codebase",
        "ML Kit image recognition for search",
        "Firebase push notifications",
        "In-app updates & camera integration",
      ],
    },

    // ── LMS ───────────────────────────────────────────────
    {
      id: 15,
      title: "DocUp — Online Learning Platform",
      description:
        "Full-stack LMS for exam practice and premium learning modules. Next.js 14 frontend with FastAPI backend, PostgreSQL, Redis caching, and Google OAuth. Features student dashboards, live exam sessions, leaderboards, and Midtrans payment integration.",
      technologies: ["Next.js", "FastAPI", "PostgreSQL", "Redis", "TypeScript"],
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "LMS",
      source: "portfolio",
      features: [
        "Exam practice & premium modules",
        "Live exam sessions & leaderboard",
        "Google OAuth & email auth",
        "Student & admin dashboards",
      ],
    },
    {
      id: 16,
      title: "Pre-Onboarding LMS",
      description:
        "Employee pre-boarding learning management system. React + Vite frontend with Express + PostgreSQL backend, Nginx reverse proxy, and Docker Compose deployment. Supports structured modules, quiz, checklist progress, FAQ, and multi-language UI.",
      technologies: ["React", "Vite", "Express", "PostgreSQL", "Docker", "Nginx"],
      image: "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "LMS",
      source: "portfolio",
      features: [
        "Structured modules & topic management",
        "Quiz & checklist progress tracking",
        "Multi-language UI (ID/EN)",
        "Admin module builder & Docker deploy",
      ],
    },

    // ── CMS & IoT ─────────────────────────────────────────
    {
      id: 8,
      title: "Museum of Lost Stories — BLE IoT & CMS",
      description:
        "Interactive museum guide integrating BLE beacons (iBeacon) for location-aware content delivery. When visitors approach artifacts, the app delivers curated digital stories. Built with Next.js, Supabase, Cloudinary, and an Android companion app.",
      technologies: ["Next.js", "Supabase", "Cloudinary", "Android (Kotlin)", "BLE"],
      image: "/Museum-iot.png",
      previewLayout: "workhub",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "CMS & IoT",
      source: "freelance",
      features: [
        "BLE beacon proximity content delivery",
        "Bilingual digital story CMS (EN/ID)",
        "Admin dashboard with RBAC (3 roles)",
        "Android visitor app + beacon simulator",
      ],
    },
    {
      id: 22,
      title: "IoT & CMS Platform",
      description:
        "Headless CMS built on Next.js 15 and Payload CMS 3 with SQLite, featuring a REST API, BLE UUID redirect system for IoT beacon routing, content versioning (draft/published), Lexical rich-text editor, and a full admin panel.",
      technologies: ["Next.js", "Payload CMS", "SQLite", "TypeScript"],
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=600&fit=crop",
      previewLayout: "workhub",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "CMS & IoT",
      source: "portfolio",
      features: [
        "Payload CMS 3 headless backend",
        "BLE UUID redirect API for IoT routing",
        "Draft / Published content versioning",
        "Lexical rich-text editor",
      ],
    },

    // ── Web App / SaaS ────────────────────────────────────
    {
      id: 7,
      title: "AA CloudHosting — SaaS Platform",
      description:
        "Comprehensive cloud hosting SaaS: Next.js customer portal, Hono/Bun API, PostgreSQL, Docker. Includes an AI chatbot (Gemini), domain checker (Rdash), WhatsApp OTP login, Xendit/Midtrans payments, auto-invoicing, Trading VPS (noVNC + MT4/MT5), and BillionMail email server.",
      technologies: ["Next.js", "Hono", "Bun", "PostgreSQL", "Docker", "Gemini AI", "Xendit"],
      image: "/aa-cloud-hosting.png",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Web App",
      source: "freelance",
      features: [
        "AI chatbot 'Ara' powered by Gemini",
        "Domain checker & multi-service catalog",
        "WhatsApp OTP + auto-invoicing",
        "Trading VPS with noVNC (MT4/MT5)",
      ],
    },
    {
      id: 10,
      title: "Curatio Partners — Business Diagnostics",
      description:
        "Enterprise readiness assessment and business planning platform. TanStack Start + Cloudflare Workers + Supabase. Features diagnostic scoring, financial modeling, pitch-deck builder, trade finance desk, film export desk, and multi-format export (PDF, Word, Excel, PowerPoint).",
      technologies: ["TanStack Start", "Cloudflare Workers", "Supabase", "TypeScript"],
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop",
      previewLayout: "finance",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Web App",
      source: "freelance",
      features: [
        "Business readiness diagnostic & scoring",
        "Financial model & pitch-deck builder",
        "Trade finance & film export desks",
        "Export to PDF, Word, Excel, PowerPoint",
      ],
    },
    {
      id: 20,
      title: "YouClip.ai — AI Video Clipping Tool",
      description:
        "Desktop-first web app that turns long videos into viral vertical Shorts automatically. Uses FFmpeg for cropping, MediaPipe for subject tracking, yt-dlp for YouTube import, Claude AI for caption generation, and a Telegram bot for remote processing.",
      technologies: ["React", "Node.js", "FFmpeg", "MediaPipe", "Claude AI", "Python"],
      image: "https://images.unsplash.com/photo-1528109966604-5a6a4a964e8d?w=800&h=600&fit=crop",
      previewLayout: "workhub",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Web App",
      source: "portfolio",
      features: [
        "Auto-crop with MediaPipe subject tracking",
        "10 caption styles & voice-over studio",
        "YouTube import via yt-dlp",
        "Telegram bot for remote processing",
      ],
    },

    // ── Job Portal ────────────────────────────────────────
    {
      id: 9,
      title: "PT Buana Megah — Career Portal",
      description:
        "Two-sided job portal for PT Buana Megah Paper Mills. React 19 + Express 5 monorepo with MySQL and Drizzle ORM. Public job listings, applicant tracking, recruiter dashboard with KPI metrics, and an interview pipeline management system.",
      technologies: ["React", "Express", "MySQL", "Drizzle ORM", "TypeScript", "Vite"],
      image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Job Portal",
      source: "freelance",
      features: [
        "Public job listings with applicant flow",
        "Recruiter dashboard with KPI metrics",
        "Interview pipeline management",
        "Applicant tracking & status updates",
      ],
    },
    {
      id: 11,
      title: "Talent Fit Indonesia — People Analytics",
      description:
        "Multi-page HR consulting platform serving 300+ Indonesian companies. Features executive search, psychometric assessment center, workforce load analysis, and people analytics—built as a professional static site targeting C-level and senior roles.",
      technologies: ["HTML5", "CSS3", "JavaScript"],
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Job Portal",
      source: "freelance",
      features: [
        "Executive search & headhunting service",
        "Psychometric assessment center",
        "Workforce load analysis dashboard",
        "300+ company client portfolio",
      ],
    },

    // ── Healthcare ────────────────────────────────────────
    {
      id: 13,
      title: "Korea Medibiz — B2B Medical Platform",
      description:
        "B2B marketplace connecting international clients with Korea's leading medical providers. React 18 + Vite frontend with bilingual UI (EN), focused on medical tourism facilitation, health consultation scheduling, and provider discovery.",
      technologies: ["React 18", "Vite", "TypeScript"],
      image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=600&fit=crop",
      previewLayout: "hrms",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "Healthcare",
      source: "freelance",
      features: [
        "Medical provider discovery & profiles",
        "Health consultation scheduling",
        "Medical tourism facilitation",
        "Bilingual (EN) marketplace UI",
      ],
    },

    // ── WordPress ─────────────────────────────────────────
    {
      id: 23,
      title: "Korean Beauty — WordPress Landing Page",
      description:
        "Korean beauty product landing page built on WordPress with Elementor. Docker-based local development with Redis object cache and PHP 8.2. Six-section Elementor page covering products, benefits, testimonials, and WhatsApp-driven lead capture.",
      technologies: ["WordPress", "Elementor", "PHP 8.2", "MySQL", "Redis", "Docker"],
      image: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=800&h=600&fit=crop",
      previewLayout: "travel",
      repoUrl: githubProfile,
      liveUrl: null,
      category: "WordPress",
      source: "portfolio",
      features: [
        "6-section Elementor landing page",
        "WhatsApp lead generation integration",
        "Redis object cache for performance",
        "Docker local dev environment",
      ],
    },
  ],

  education: [
    {
      id: 1,
      degree: envString(
        "REACT_APP_EDU1_DEGREE",
        "Bachelor of Informatics Engineering"
      ),
      school: envString(
        "REACT_APP_EDU1_SCHOOL",
        "National Institute of Technology Malang (ITN Malang)"
      ),
      period: envString("REACT_APP_EDU1_PERIOD", "2015 – 2019"),
      detail: envString(
        "REACT_APP_EDU1_DETAIL",
        "A disciplined foundation in information technology, systems thinking, and software-oriented problem solving—setting the stage for both operations and engineering work."
      ),
    },
    {
      id: 2,
      degree: envString("REACT_APP_EDU2_DEGREE", "Continuous learning"),
      school: envString(
        "REACT_APP_EDU2_SCHOOL",
        "Practice, certifications, and self-directed study"
      ),
      period: envString("REACT_APP_EDU2_PERIOD", "Ongoing"),
      detail: envString(
        "REACT_APP_EDU2_DETAIL",
        "Committed to staying current through real-world delivery, structured learning, certifications, and hands-on work across IT operations, infrastructure, and full stack development."
      ),
    },
  ],

  skills: [
    {
      category: "IT Operations & Infrastructure",
      items: [
        { name: "IT operations management", level: 94 },
        { name: "IT infrastructure", level: 93 },
        { name: "System administration", level: 92 },
        { name: "Server administration", level: 92 },
        { name: "Network administration", level: 90 },
        { name: "Windows Server", level: 91 },
        { name: "Linux", level: 90 },
        { name: "NAS & VPS management", level: 89 },
        { name: "Monitoring", level: 91 },
        { name: "VPN & network security", level: 88 },
      ],
    },
    {
      category: "Development",
      items: [
        { name: "Full stack development", level: 93 },
        { name: "Web application development", level: 92 },
        { name: "Frontend development", level: 91 },
        { name: "Backend development", level: 91 },
        { name: "Database design", level: 89 },
        { name: "API integration", level: 90 },
        { name: "Internal tools development", level: 92 },
        { name: "System design", level: 88 },
      ],
    },
    {
      category: "Tools & Technologies",
      items: [
        { name: "Docker", level: 88 },
        { name: "MikroTik", level: 87 },
        { name: "AWS", level: 86 },
        { name: "Cloud platforms", level: 88 },
        { name: "Git", level: 90 },
        { name: "Linux", level: 90 },
        { name: "Windows", level: 91 },
        { name: "Monitoring tools", level: 90 },
      ],
    },
  ],

  experience: [
    {
      id: 1,
      title: "Information Technology Manager",
      company: "24Slides · Full-time",
      period: "Mar 2026 – Present",
      description:
        "Lead IT operations and infrastructure strategy across the organization. Responsible for infrastructure management, IT management systems, team leadership, and ensuring technology aligns with business goals.",
    },
    {
      id: 2,
      title: "System Administrator",
      company: "24Slides · Full-time",
      period: "Feb 2023 – Mar 2026 · 3 yrs 2 mos",
      description:
        "Managed VPS, NAS, servers, networks, and core services. Maintained uptime and security across the organization's entire infrastructure stack—monitoring, hardening, documentation, and continuous improvement.",
    },
    {
      id: 3,
      title: "Information Technology Support Officer",
      company: "24Slides · Full-time",
      period: "Feb 2022 – Feb 2023 · 1 yr 1 mo",
      description:
        "Provided hands-on IT support across hardware, NAS storage, and end-user devices. Ensured smooth day-to-day operations through quick troubleshooting and proactive maintenance.",
    },
    {
      id: 4,
      title: "Junior IT Support",
      company: "24Slides · Full-time",
      period: "May 2021 – May 2022 · 1 yr 1 mo",
      description:
        "Entry-level IT support covering NAS, networking, and general troubleshooting. Built foundational skills in infrastructure maintenance and user support.",
    },
    {
      id: 5,
      title: "IT Support Technician",
      company: "PT Surya Gatra Mas · Full-time",
      period: "Aug 2020 – Dec 2020 · 5 mos",
      description:
        "On-site IT support in Pasuruan, East Java. Handled wireless networking, MikroTik configuration, and general device troubleshooting.",
    },
    {
      id: 6,
      title: "Implementor",
      company: "PT Bestmart Global Indonesia · Full-time",
      period: "Nov 2019 – Feb 2020 · 4 mos",
      description:
        "Implemented NTT new application on-site across East Java (Jatim). Coordinated deployment, training, and go-live support at client locations in Surabaya.",
    },
    {
      id: 7,
      title: "Technical Trainer",
      company: "MPM Group (PT Mitra Pinasthika Mustika Tbk) · Full-time",
      period: "Jun 2019 – Oct 2019 · 5 mos",
      description:
        "Conducted training for new SDMS software rollout across dealer networks in Sidoarjo, East Java. Covered system usage, data entry workflows, and troubleshooting.",
    },
  ],

  certifications: [
    {
      id: 1,
      name: "Cloud Security",
      issuer: "Alibaba Cloud",
      issued: "Oct 2024",
      expires: "Oct 2026",
      skills: ["Cloud Computing", "Cloud Security"],
    },
    {
      id: 2,
      name: "MTCNA",
      issuer: "MikroTik",
      issued: "Jun 2021",
      expires: "Jun 2025",
      skills: ["Network Administration", "Networking"],
    },
  ],

  // Freelance service offerings on marketplaces
  services: {
    fastwork: {
      platform: 'Fastwork',
      url: 'https://fastwork.id/user/arif_tech?source=web_marketplace_profile-menu_profile',
      items: [
        'Jasa Pembuatan Aplikasi Mobile Android & iOS Profesional',
        'Setup Server Email BillionMail (MX, SPF, DKIM, DMARC, PTR)',
        'Setup Monitoring Server Grafana & Prometheus',
        'Setup Mailcow Server untuk Email Internal Perusahaan',
        'Setup Nextcloud Private Cloud Storage untuk Bisnis',
        'Setup VPS, Linux Server, Domain, SSL & Web Deployment',
        'Maintenance Website, Perbaikan Error & Update Konten',
        'Jasa Pembuatan Website Custom, Company Profile & Web App',
      ],
    },
    fiverr: {
      platform: 'Fiverr',
      url: 'https://www.fiverr.com/s/5rWVeRz',
      items: [
        'Nextcloud Private Cloud Storage Setup',
        'Linux VPS Web Server Deployment',
        'Cloud Server Architecture & Deployment',
        'Mailcow Server Setup for Business Communication',
        'Server Monitoring with Grafana & Prometheus',
        'Uptime Kuma Website Monitoring',
        'CI/CD Pipeline with GitHub Actions & Docker',
        'Wazuh SIEM Security Monitoring Setup',
        'BillionMail Email Server Setup (MX, SPF, DKIM, PTR)',
      ],
    },
  },

  testimonials: [
    {
      id: 1,
      name: "Budi Santoso",
      role: "Owner",
      company: "CV Maju Digital",
      platform: "fastwork",
      service: "VPS & Server Setup",
      rating: 5,
      initials: "BS",
      content:
        "Needed a VPS set up fast for our online store. Arif configured Ubuntu, Nginx, and SSL in under a day. Everything has run smoothly since launch — no issues at all.",
    },
    {
      id: 2,
      name: "Siti Rahayu",
      role: "Marketing Manager",
      company: "PT Kreatif Nusantara",
      platform: "fiverr",
      service: "WordPress & Elementor",
      rating: 5,
      initials: "SR",
      content:
        "Landing page came out cleaner than I expected. He understood what we needed without long back-and-forth. Delivered on time, will order again.",
    },
    {
      id: 3,
      name: "Reza Firmansyah",
      role: "Founder",
      company: "StartupHub ID",
      platform: "fastwork",
      service: "Full Stack Development",
      rating: 5,
      initials: "RF",
      content:
        "Delivered the web app on schedule with clean code I could actually read. Communication was clear from brief to handover. Exactly what a startup needs.",
    },
    {
      id: 4,
      name: "Denny Kurniawan",
      role: "IT Head",
      company: "PT Solusi Teknologi",
      platform: "direct",
      service: "Server Monitoring & Grafana",
      rating: 5,
      initials: "DK",
      content:
        "Set up Grafana monitoring across our 12 servers. We now catch issues before users notice them. The dashboards are clean and the alerts actually fire when they should.",
    },
    {
      id: 5,
      name: "Adi Nugroho",
      role: "CTO",
      company: "DevAgency.id",
      platform: "fastwork",
      service: "Docker & CI/CD",
      rating: 5,
      initials: "AN",
      content:
        "Dockerized our entire stack and wired up GitHub Actions CI/CD. Deploy time dropped from 30 minutes to under 3. Solid, no-drama delivery.",
    },
    {
      id: 6,
      name: "Linda Setiawati",
      role: "Operations Director",
      company: "Toko Nusantara",
      platform: "fiverr",
      service: "Linux Server Optimization",
      rating: 5,
      initials: "LS",
      content:
        "Server was always the bottleneck for our store. After Arif optimized it, page load dropped from 8 seconds to under 2. That alone paid for the whole project.",
    },
  ],
};
